<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ResizableTextArea from 'shared/components/ResizableTextArea.vue';
import Avatar from 'next/avatar/Avatar.vue';
import FluentIcon from 'shared/components/FluentIcon/Index.vue';

const props = defineProps({
  config: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();

const isInputFocused = ref(false);

const getStatusText = computed(() => {
  return props.config.isOnline
    ? t('INBOX_MGMT.WIDGET_BUILDER.BODY.TEAM_AVAILABILITY.ONLINE')
    : t('INBOX_MGMT.WIDGET_BUILDER.BODY.TEAM_AVAILABILITY.OFFLINE');
});

const channelConfig = {
  whatsapp: { label: 'WhatsApp' },
  messenger: { label: 'Facebook Messenger' },
  instagram: { label: 'Instagram' },
  telegram: { label: 'Telegram' },
};

const enabledChannels = computed(() => {
  const channels = props.config.externalChannels || {};
  return Object.entries(channels)
    .filter(([, value]) => value && value.enabled && value.url)
    .map(([key, value]) => ({
      key,
      url: value.url,
      ...channelConfig[key],
    }));
});
</script>

<template>
  <div class="relative flex flex-col w-full px-4 gap-3">
    <!-- External Channels Section - Same design as widget -->
    <div
      v-if="config.isDefaultScreen && enabledChannels.length > 0"
      class="flex flex-col w-full shadow-sm rounded-lg bg-n-background dark:bg-n-solid-2 overflow-hidden"
    >
      <!-- Header -->
      <div
        class="px-4 py-2 text-xs font-semibold text-white"
        :style="{ backgroundColor: config.color }"
      >
        {{ $t('INBOX_MGMT.WIDGET_BUILDER.EXTERNAL_CHANNELS.SELECT_CHANNEL') }}
      </div>

      <!-- Channel List -->
      <div class="flex flex-col">
        <a
          v-for="channel in enabledChannels"
          :key="channel.key"
          :href="channel.url"
          target="_blank"
          rel="noopener noreferrer"
          class="flex items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-n-alpha-1 border-b border-n-container last:border-b-0"
        >
          <span
            class="flex items-center justify-center w-6 h-6 rounded-full text-white text-xs"
            :style="{ backgroundColor: config.color }"
          >
            {{ channel.label.charAt(0) }}
          </span>
          <span class="text-xs font-medium text-n-slate-12">{{
            channel.label
          }}</span>
          <FluentIcon
            icon="chevron-right"
            size="12"
            class="ml-auto text-n-slate-11"
          />
        </a>
      </div>
    </div>

    <!-- Team Availability / Start Conversation -->
    <div
      v-if="config.isDefaultScreen"
      class="p-4 rounded-lg shadow-sm bg-n-background dark:bg-n-solid-2"
    >
      <div class="flex items-center justify-between">
        <div>
          <div
            class="text-sm font-medium leading-4 text-n-slate-12 dark:text-n-slate-50"
          >
            {{ getStatusText }}
          </div>
          <div class="mt-1 text-xs text-n-slate-11">
            {{ config.replyTime }}
          </div>
        </div>
        <Avatar name="C" :size="34" rounded-full />
      </div>
      <button
        v-if="config.isDefaultScreen"
        class="inline-flex items-center justify-between px-2 py-1 mt-1 -ml-2 font-medium leading-6 bg-transparent rounded-md text-n-slate-12 dark:bg-transparent"
        :style="{ color: config.color }"
      >
        <span class="pr-2 text-xs">
          {{
            $t(
              'INBOX_MGMT.WIDGET_BUILDER.FOOTER.START_CONVERSATION_BUTTON_TEXT'
            )
          }}
        </span>
        <FluentIcon icon="arrow-right" size="14" />
      </button>
    </div>
    <div
      v-else
      class="flex items-center h-10 bg-white rounded-md dark:!bg-n-slate-3"
      :class="{ 'ring-2 ring-n-brand dark:ring-n-brand': isInputFocused }"
    >
      <ResizableTextArea
        id="chat-input"
        :rows="1"
        :placeholder="
          $t('INBOX_MGMT.WIDGET_BUILDER.FOOTER.CHAT_INPUT_PLACEHOLDER')
        "
        class="flex-grow !bg-white border-0 outline-none !outline-0 border-none h-8 text-sm dark:!bg-n-slate-3 pb-0 !pt-1.5 resize-none px-3 !mb-0 focus:outline-none rounded-md"
        @focus="isInputFocused = true"
        @blur="isInputFocused = false"
      />
      <div class="flex items-center gap-2 px-2">
        <FluentIcon icon="emoji" />
        <FluentIcon class="icon-send" icon="send" />
      </div>
    </div>
  </div>
</template>
